<template>
  <SoCheckbox
    ref="customer-reading-checkbox"
    v-model="isCustomerReadFilterApplied"
    label="Only Customer Readings"
    class="mt-2"
  />
  <SoDataTable
    ref="reading-list-table"
    :headers="readingsTableHeaders"
    :items="read"
    :searchable="false"
    compact
    class="mt-4"
  />
  <div class="reading-history__chart mt-4">
    <SoLineChart
      ref="reading-chart"
      title="Readings history"
      :data-sets="chartDataSets"
      :labels="chartLabels"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoLineChart from "@/components/charts/SoLineChart.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import {
    SoDataTableColumnType,
    SoDataTableHeader,
  } from "@/types/components/SoDataTable"
  import { IRegister } from "@/models/meterRegister/meterRegister.types"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import dayjs from "dayjs"
  import { useThemeHelper } from "@/theme/themeHelper"
  import { COLOR_PALETTE } from "@/theme/colorPalette"
  import { READING_TYPE } from "@/constants/meterpoints"
  import { Status } from "@/types/Status"

  const readingsTableHeaders: SoDataTableHeader[] = [
    {
      title: "Reading Date",
      key: "date",
      type: SoDataTableColumnType.DATE,
    },
    {
      title: "Meter Serial Number",
      key: "meterIdentifier",
    },
    {
      key: "status",
      title: "Status",
      type: SoDataTableColumnType.STATUSES,
    },
    {
      title: "Source",
      key: "source",
    },
    {
      title: "Meter Reading",
      key: "cumulative",
    },
    {
      title: "Consumption",
      key: "consumption",
    },
    {
      title: "Unit",
      key: "unit",
    },
  ]

  export default defineComponent({
    components: { SoLineChart, SoDataTable, SoCheckbox },
    props: {
      register: {
        type: Object as PropType<IRegister>,
        required: true,
      },
    },
    data() {
      return {
        current: useCurrentAccountStore(),
        currentTheme: useThemeHelper(),
        readingsTableHeaders,
        isCustomerReadFilterApplied: false,
      }
    },
    computed: {
      read() {
        return this.readingsSortedAndOrdered
          .map((reading) => {
            const status = [
              {
                value: reading.statusCode,
                props: { status: reading.status },
              },
            ]

            if (
              reading.sequenceType === "First" ||
              reading.sequenceType === "Last"
            ) {
              status.push({
                value: reading.sequenceType,
                props: {
                  status: new Status(
                    reading.sequenceType === "First" ? "neutral" : "default",
                    reading.sequenceType,
                  ),
                },
              })
            }

            return {
              ...reading,
              meterIdentifier: reading.meterIdentifier,
              status,
              rowColor:
                reading.source === READING_TYPE.CUSTOMER
                  ? this.currentTheme.colors["success-highlight"]
                  : "",
            }
          })
          .sort((a, b) =>
            b.date.isSame(a.date) ? b.id - a.id : b.date.diff(a.date),
          )
      },
      chartLabels() {
        return this.readingsThisYear.map((reading) => reading.date)
      },
      chartDataSets() {
        const allReadings = this.readingsThisYear.map(
          (reading) => reading.cumulative,
        )

        const getReadingDataset = (
          label,
          filterFn,
          color,
          useCross = true,
        ) => ({
          label,
          lineColor: color,
          pointStyle: useCross ? "crossRot" : "point",
          hideLine: true,
          data: this.readingsThisYear.map((reading) =>
            filterFn(reading) ? reading.cumulative : null,
          ),
        })

        return [
          {
            label: "Reading",
            lineColor: COLOR_PALETTE.green400,
            data: allReadings,
            hidePoints: true,
          },
          getReadingDataset(
            "Customer Reading",
            (reading) => reading.isCustomerReading,
            "blue",
            false,
          ),
          getReadingDataset(
            "Smart Reading",
            (reading) => reading.isSmartReading,
            "orange",
          ),
          getReadingDataset(
            "Manual Reading",
            (reading) => reading.isManualReading,
            "purple",
          ),
          getReadingDataset(
            "System Reading",
            (reading) => reading.isSystemReading,
            "cyan",
          ),
        ]
      },
      readingsSortedAndOrdered() {
        return this.isCustomerReadFilterApplied
          ? this.readingsSorted.filter((reading) => reading.isCustomerReading)
          : this.readingsSorted
      },
      readingsSorted() {
        const readings = this.register.readings
        if (!readings?.length) return []
        return [...readings].sort((a, b) =>
          a.date.isSame(b.date) ? a.id - b.id : a.date.diff(b.date),
        )
      },
      readingsThisYear() {
        return this.readingsSorted.filter((reading) =>
          reading.date.isAfter(dayjs().subtract(1, "year")),
        )
      },
    },
  })
</script>

<style scoped>
  .reading-history__chart {
    height: 400px;
  }
</style>
